import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CalendarioAnualFerias = ({ anoInicial = new Date().getFullYear(), diasSelecionados = [], onDiasChange }) => {
  const [ano, setAno] = useState(anoInicial);
  const [diasSelecionadosLocal, setDiasSelecionadosLocal] = useState(diasSelecionados);

  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Feriados fixos e móveis em Portugal
  const getFeriados = (ano) => {
    const feriados = [];
    
    // Feriados fixos nacionais
    feriados.push(`${ano}-01-01`); // Ano Novo
    feriados.push(`${ano}-04-25`); // 25 de Abril
    feriados.push(`${ano}-05-01`); // Dia do Trabalhador
    feriados.push(`${ano}-06-10`); // Dia de Portugal
    feriados.push(`${ano}-08-15`); // Assunção de Nossa Senhora
    feriados.push(`${ano}-08-20`); // Feriado Municipal (20 de Agosto)
    feriados.push(`${ano}-10-05`); // Implantação da República
    feriados.push(`${ano}-11-01`); // Todos os Santos
    feriados.push(`${ano}-12-01`); // Restauração da Independência
    feriados.push(`${ano}-12-08`); // Imaculada Conceição
    feriados.push(`${ano}-12-25`); // Natal
    
    // Páscoa e feriados móveis (cálculo simplificado)
    // Para uma implementação completa, usar biblioteca de cálculo de Páscoa
    const pascoa = calcularPascoa(ano);
    feriados.push(pascoa); // Páscoa
    feriados.push(adicionarDias(pascoa, -47)); // Carnaval (Terça-feira)
    feriados.push(adicionarDias(pascoa, -2)); // Sexta-feira Santa
    feriados.push(adicionarDias(pascoa, 60)); // Corpo de Deus
    
    return feriados;
  };
  
  // Calcular data da Páscoa (algoritmo de Meeus/Jones/Butcher)
  const calcularPascoa = (ano) => {
    const a = ano % 19;
    const b = Math.floor(ano / 100);
    const c = ano % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const mes = Math.floor((h + l - 7 * m + 114) / 31);
    const dia = ((h + l - 7 * m + 114) % 31) + 1;
    return `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
  };
  
  // Adicionar dias a uma data
  const adicionarDias = (dataStr, dias) => {
    const data = new Date(dataStr + 'T00:00:00');
    data.setDate(data.getDate() + dias);
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  };

  const getDiasNoMes = (mes, ano) => {
    return new Date(ano, mes + 1, 0).getDate();
  };

  const getPrimeiroDiaSemana = (mes, ano) => {
    return new Date(ano, mes, 1).getDay();
  };

  const toggleDia = (data) => {
    const novosDias = diasSelecionadosLocal.includes(data)
      ? diasSelecionadosLocal.filter(d => d !== data)
      : [...diasSelecionadosLocal, data];
    
    setDiasSelecionadosLocal(novosDias);
    if (onDiasChange) {
      onDiasChange(novosDias);
    }
  };

  const isDiaFimSemana = (dia, mes, ano) => {
    const diaSemana = new Date(ano, mes, dia).getDay();
    return diaSemana === 0 || diaSemana === 6; // Domingo ou Sábado
  };
  
  const isDiaFeriado = (data) => {
    const feriadosAno = getFeriados(ano);
    return feriadosAno.includes(data);
  };
  
  // Contar APENAS dias úteis (excluindo fins de semana e feriados)
  const contarDiasUteis = () => {
    return diasSelecionadosLocal.filter(data => {
      const [ano, mes, dia] = data.split('-').map(Number);
      const isFimSemana = isDiaFimSemana(dia, mes - 1, ano);
      const isFeriado = isDiaFeriado(data);
      return !isFimSemana && !isFeriado;
    }).length;
  };

  const renderMes = (mesIndex) => {
    const diasNoMes = getDiasNoMes(mesIndex, ano);
    const primeiroDia = getPrimeiroDiaSemana(mesIndex, ano);
    const dias = [];

    // Espaços vazios antes do primeiro dia
    for (let i = 0; i < primeiroDia; i++) {
      dias.push(<div key={`vazio-${i}`} className="h-8"></div>);
    }

    // Dias do mês
    for (let dia = 1; dia <= diasNoMes; dia++) {
      const data = `${ano}-${String(mesIndex + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
      const isSelecionado = diasSelecionadosLocal.includes(data);
      const isFimSemana = isDiaFimSemana(dia, mesIndex, ano);
      const isFeriado = isDiaFeriado(data);

      dias.push(
        <button
          key={dia}
          onClick={() => toggleDia(data)}
          title={isFeriado ? 'Feriado' : ''}
          className={`
            h-8 w-full text-sm rounded transition-all flex items-center justify-center font-medium
            ${isSelecionado ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md' : ''}
            ${isFeriado && !isSelecionado ? 'bg-yellow-100 text-yellow-800 border border-yellow-400' : ''}
            ${isFimSemana && !isSelecionado && !isFeriado ? 'bg-gray-100 text-gray-400' : ''}
            ${!isFimSemana && !isSelecionado && !isFeriado ? 'bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50' : ''}
          `}
        >
          {dia}
        </button>
      );
    }

    return (
      <Card key={mesIndex} className="shadow-sm">
        <CardContent className="p-3">
          <div className="text-center font-bold mb-3 text-sm bg-gradient-to-r from-blue-600 to-blue-400 text-white py-2 rounded-md">
            {meses[mesIndex]}
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2 font-semibold">
            <div className="text-red-500">D</div>
            <div className="text-gray-600">S</div>
            <div className="text-gray-600">T</div>
            <div className="text-gray-600">Q</div>
            <div className="text-gray-600">Q</div>
            <div className="text-gray-600">S</div>
            <div className="text-red-500">S</div>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {dias}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      {/* Controle de Ano */}
      <div className="flex items-center justify-between">
        <Button onClick={() => setAno(ano - 1)} variant="outline" size="sm">
          <ChevronLeft className="h-4 w-4" />
          {ano - 1}
        </Button>
        <h2 className="text-lg font-semibold">{ano}</h2>
        <Button onClick={() => setAno(ano + 1)} variant="outline" size="sm">
          {ano + 1}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Painel de Resumo Compacto */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-4 justify-between">
          {/* Dias Úteis */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
              ✓
            </div>
            <div>
              <div className="text-xl font-bold text-green-700">{contarDiasUteis()}</div>
              <div className="text-xs text-green-600">Dias úteis</div>
            </div>
          </div>

          {/* Total Selecionado */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center"></div>
            <div>
              <div className="text-xl font-bold text-blue-700">{diasSelecionadosLocal.length}</div>
              <div className="text-xs text-blue-600">Total dias</div>
            </div>
          </div>

          {/* Legenda */}
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-gray-100 rounded border"></div>
              <span className="text-gray-500">Fim semana</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-yellow-100 rounded border border-yellow-400">🎉</div>
              <span className="text-yellow-700">Feriado</span>
            </div>
          </div>
        </div>
      </div>

      {/* Layout de Calendário - 3 meses por linha para mais espaço */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(mes => renderMes(mes))}
      </div>

      {/* Botões de Ação */}
      {diasSelecionadosLocal.length > 0 && (
        <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
          <div className="text-sm">
            <span className="font-medium text-green-700">{contarDiasUteis()} dias úteis</span>
            <span className="text-gray-500"> serão descontados</span>
          </div>
          <Button 
            onClick={() => {
              setDiasSelecionadosLocal([]);
              if (onDiasChange) onDiasChange([]);
            }}
            variant="outline"
            size="sm"
          >
            Limpar
          </Button>
        </div>
      )}
    </div>
  );
};

export default CalendarioAnualFerias;
